import React, {useEffect, useState} from "react"
import classNames from "classnames"
import {useTranslation} from "react-i18next"
import {RouteComponentProps} from "react-router"
import {Document, Page, pdfjs} from "react-pdf";
import {Container, Button, CircularProgress, Typography, Paper, useTheme, useMediaQuery} from "@material-ui/core"

// HOCs
import {WithUserProps} from "../../../auth/hoc/withUser"
import {WithRecordsProps} from "../../hoc/withRecords"
import {WithRecordDataProps} from "../../hoc/withRecordData"

// Components
import InitDialog from "../InitDialog"
import GoBackSection from "../GoBackSection"
import ApproveSection from "../ApproveSection"
import RecordDetailsSection from "../RecordDetailsSection"
import RecordDetailsSectionMobile from "../RecordDetailsSectionMobile"

// Constants
import {AppRoute} from "../../../routing/constants"
import {RecordStatusEnum} from "../../../doctor-stats/models"

// Styles
import useStyles from "./styles"

// Utils
import Utils from "../../../../utils/Utils"

// Models
import {RecordTypeEnum} from "../../models"

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

type RecordPageProps =
    WithUserProps &
    WithRecordsProps &
    WithRecordDataProps &
    RouteComponentProps

/**
 * @param props
 */
export default function RecordPage(props: RecordPageProps) {
    const [t] = useTranslation("common");
    const theme = useTheme();
    const styles = useStyles();
    const matches = useMediaQuery(theme.breakpoints.down("sm"));
    const {
        pdf,
        user,
        history,
        metaData,
        fetchingPDF,
        recordConflict,
        currentRecord,
        fetchingRecord,
    } = props;

    const [numPages, setNumPages] = useState(0);

    
    if (fetchingRecord) {
        return (
            <CircularProgress className={styles.loading_spinner} size={50}/>
        )
    }

    if (currentRecord === null) {
        return renderNothingToReview()
    }

    const isEditable = false === Utils.isCompanyMaster(user) && currentRecord.status !== RecordStatusEnum.APPROVED

    function renderNothingToReview() {
        return (
            <Container maxWidth="sm" className={styles.nothing_to_review_container}>
                <Typography component="h1" variant="h5">{t("records.nothing_to_review")}</Typography>
                <Button color="primary"
                        variant="contained"
                        onClick={() => history.replace(AppRoute.Home)}
                        className={styles.nothing_to_review_back_button}>
                    {t("back")}
                </Button>
            </Container>
        )
    }

    function renderPediatricLabel() {
        if (metaData === null || metaData.age === null || metaData.age >= 18) return null

        return (
            <div className={styles.pediatric_label}>
                <span>{t("records.pediatric")}</span>
            </div>
        )
    }

    function renderECGView() {
        return (
            <Paper className={styles.paper}>
                {
                    matches ?
                    <RecordDetailsSectionMobile/> :
                    <RecordDetailsSection/>
                }
            </Paper>
        )
    }

    function renderPDF() {
        return (
            <>
                {
                    pdf && !fetchingPDF &&
                    <div className={classNames(styles.pdf_wrap)}>
                        <Document
                            className={classNames(styles.pdf)}
                            file={`data:application/pdf;base64,${pdf}`}
                            onLoadSuccess={({numPages}) => setNumPages(numPages)}
                        >
                            {renderPDFPages()}
                        </Document>
                    </div>
                }
                {
                    fetchingPDF &&
                    <CircularProgress className={styles.loading_spinner} size={30}/>
                }
            </>
        )
    }

    function renderPDFPages() {
        if (numPages === 0) {
            return null;
        }
        const pages = [];
        for (let i = 1; i < numPages; i++) {
            pages.push(
                <Page
                    key={`page_${i}`}
                    pageNumber={i}
                    className={classNames(styles.pdf_item)}
                />
            );
        }
        return [...pages];
    };

    function handleEdit() {
        const url = `cardiolyse://holter?ecgId=${currentRecord?.ecgId}&teleRecordId=${currentRecord?.teleRecordId}`
        console.log(url)
        const win = window.open(url, "_blank");
        if (win) {
            win.focus();
        }
    }

    return (
        <Container maxWidth={false} className={styles.container}>
            {renderPediatricLabel()}
            <div className={classNames(styles.top_panel, !isEditable && styles.top_panel__centered)}>
                {
                    isEditable &&
                    <GoBackSection/>
                }
                <div className={classNames(styles.record_name, !isEditable && styles.record_name__preview)}>
                    {currentRecord.fileName}
                    <InitDialog/>
                </div>
                <div className={styles.btns_wrap}>
                    {
                        (currentRecord.recordType === RecordTypeEnum.HOLTER && currentRecord.ecgId && currentRecord.teleRecordId) &&
                        <Button
                            color="secondary"
                            variant="contained"
                            onClick={handleEdit}
                            className={styles.edit_button}
                        >
                            {t("records.edit")}
                        </Button>
                    }
                    {
                        (isEditable && currentRecord.recordType != RecordTypeEnum.HOLTER) &&
                        <ApproveSection/>
                    }
                </div>
            </div>
            {/* {
                currentRecord.recordType === RecordTypeEnum.HOLTER &&
                renderPDF()
            } */}
            {
                currentRecord.recordType === RecordTypeEnum.ECG &&
                renderECGView()
            }
        </Container>
    )
}
